import { insert, remove } from "ramda";
import React from "react";
import { useIntl } from "react-intl";

import { Field } from "src/common/components/Field";
import {
	AddButton,
	AddFirstButton,
	FieldGroupList,
	FieldGroupListItem,
	RemoveButton
} from "src/common/components/FieldGroupList";
import { RowRenderer } from "src/features/forms/components/RowRenderer";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import { NodeContextProvider } from "src/features/forms/context/NodeContext";
import {
	useMaxItems,
	useMinItems,
	useValue
} from "src/features/forms/hooks/useField";
import { FieldGroupListData } from "src/features/forms/types";

export const FieldGroupListRenderer: React.FunctionComponent<FieldGroupListData> =
	React.memo(({ path, label, rows }) => (
		<NodeContextProvider path={path}>
			<ContentRenderer label={label} rows={rows} path={path} />
		</NodeContextProvider>
	));

type T = unknown;

const ContentRenderer: React.FunctionComponent<
	Pick<FieldGroupListData, "label" | "rows" | "path">
> = React.memo(({ label, rows, path }) => {
	const { formatMessage } = useIntl();

	const isLocked = React.useContext(IsLockedContext);

	const minItems = useMinItems();
	const maxItems = useMaxItems();

	const [values, setValues] = useValue<T[]>([]);

	return (
		<Field label={formatMessage(label)} path={path}>
			{values.length === 0 ? (
				<AddFirstButton
					disabled={isLocked}
					onClick={() => {
						setValues([{}]);
					}}
				/>
			) : (
				<FieldGroupList>
					{values.map((_, i) => (
						<FieldGroupListItem
							key={i}
							body={
								<NodeContextProvider path={[i]}>
									{rows.map((props, key) => (
										<RowRenderer key={key} {...props} />
									))}
								</NodeContextProvider>
							}
							footer={
								<>
									<RemoveButton
										disabled={
											isLocked ||
											values.length <= minItems
										}
										onClick={() => {
											setValues(remove(i, 1, values));
										}}
									/>
									<AddButton
										disabled={
											isLocked ||
											values.length >= maxItems
										}
										onClick={() => {
											setValues(
												insert(i + 1, {}, values)
											);
										}}
									/>
								</>
							}
						/>
					))}
				</FieldGroupList>
			)}
		</Field>
	);
});
